// RoutingRulesCard — admin-only editor for a merchant's payout routing
// rules. Backed by /v1/merchants/:id/routing-rules.
//
// Cut 3 scope: multi-candidate rules with balance-aware fallback. The
// backend's applyRouting walks candidates top-down per matched rule and
// picks the first whose available balance covers the payout amount. If
// every candidate is dry (or has no reachable balance API), the payout
// is force-queued for manual approval against the first candidate's
// rail — auto-approve is overridden to false even if the rail's
// rail-level flag says otherwise. Single-candidate rules keep the
// Cut-2 behaviour exactly (no balance check, take it).
//
// The form sits inline (no modal) so operators can scan the full rule
// list and edit any row without losing context. New rules expand the
// editor; saving collapses back into the summary chip row.

import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { apiErrorMessage } from "@/utils/apiError";
import { fmtCcyMinor } from "@/utils/format";
import { providerLabel } from "@/utils/providerLabel";
import {
  listRoutingRules,
  createRoutingRule,
  updateRoutingRule,
  deleteRoutingRule,
  type RoutingRule,
  type RoutingRuleMatch,
  type RoutingRuleCandidate,
} from "@/api/routingRules";
import type { BankAccount } from "@/api/bankAccounts";

interface Props {
  merchantId: number;
  rails: BankAccount[];
}

export function RoutingRulesCard({ merchantId, rails }: Props) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data: rules = [], isLoading, isError } = useQuery({
    queryKey: ["routing-rules", merchantId],
    queryFn: () => listRoutingRules(merchantId),
    staleTime: 30_000,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["routing-rules", merchantId] });

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <header
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--hair)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div style={{ flex: 1 }}>
          <div className="eyebrow">Routing rules</div>
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            Lowest priority is evaluated first. First match wins. Within a
            rule, candidates are walked top-down — first with sufficient
            balance is used; if every candidate is dry the payout queues
            for manual approval against the first candidate. Per-candidate
            auto-approve overrides the rail-level flag.
          </div>
        </div>
        {!adding && (
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => setAdding(true)}
          >
            <I.Plus size={11} /> Add rule
          </button>
        )}
      </header>

      <div style={{ padding: "12px 16px" }}>
        {isLoading ? (
          <div className="dim" style={{ fontSize: 12 }}>
            Loading rules…
          </div>
        ) : isError ? (
          <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
            Could not load routing rules.
          </div>
        ) : rules.length === 0 && !adding ? (
          <div className="dim" style={{ fontSize: 12 }}>
            No routing rules configured. Payouts use the operator-supplied
            rail.
          </div>
        ) : (
          <div className="stack-8">
            {rules.map((r) => (
              <RuleRow
                key={r.id}
                rule={r}
                rails={rails}
                onChanged={invalidate}
                merchantId={merchantId}
              />
            ))}
          </div>
        )}

        {adding && (
          <div style={{ marginTop: rules.length > 0 ? 12 : 0 }}>
            <RuleForm
              rails={rails}
              initial={blankRule()}
              merchantId={merchantId}
              onSaved={() => {
                invalidate();
                setAdding(false);
              }}
              onCancel={() => setAdding(false)}
            />
          </div>
        )}
      </div>
    </section>
  );
}

// ── Single-rule summary row + editor ─────────────────────────────────────

function RuleRow({
  rule,
  rails,
  merchantId,
  onChanged,
}: {
  rule: RoutingRule;
  rails: BankAccount[];
  merchantId: number;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (
      !window.confirm(
        `Delete routing rule (priority ${rule.priority})? This cannot be undone.`,
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      await deleteRoutingRule(merchantId, rule.id);
      onChanged();
    } catch {
      // Swallow; the query will refetch and surface any state mismatch.
      setDeleting(false);
    }
  }

  if (editing) {
    return (
      <RuleForm
        rails={rails}
        initial={rule}
        merchantId={merchantId}
        onSaved={() => {
          onChanged();
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div
      style={{
        padding: "10px 12px",
        border: "1px solid var(--hair, rgba(0,0,0,0.08))",
        borderRadius: 6,
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 11,
          padding: "2px 8px",
          background: "var(--bg-sunk)",
          borderRadius: 3,
        }}
      >
        #{rule.priority}
      </div>
      <div style={{ flex: 1, fontSize: 13 }}>
        <RuleSummary match={rule.match} />
        <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
          → {summariseCandidates(rule.candidates, rails)}
        </div>
      </div>
      <button
        type="button"
        className="btn ghost sm"
        onClick={() => setEditing(true)}
      >
        Edit
      </button>
      <button
        type="button"
        className="btn ghost sm"
        onClick={handleDelete}
        disabled={deleting}
        style={{ color: "var(--warn, #b42318)" }}
      >
        {deleting ? "…" : "Delete"}
      </button>
    </div>
  );
}

function RuleSummary({ match }: { match: RoutingRuleMatch }) {
  const parts: string[] = [];
  if (match.mode != null) {
    parts.push(modeLabel(match.mode));
  } else {
    parts.push("Any mode");
  }
  if (match.amount_minor_gte != null || match.amount_minor_lte != null) {
    const min =
      match.amount_minor_gte != null
        ? fmtCcyMinor(BigInt(match.amount_minor_gte), "INR")
        : "0";
    const max =
      match.amount_minor_lte != null
        ? fmtCcyMinor(BigInt(match.amount_minor_lte), "INR")
        : "∞";
    parts.push(`${min}–${max}`);
  } else {
    parts.push("Any amount");
  }
  return <span>{parts.join(" · ")}</span>;
}

function summariseCandidates(
  candidates: RoutingRuleCandidate[],
  rails: BankAccount[],
): string {
  if (candidates.length === 0) return "(no candidates)";
  return candidates
    .map((c) => {
      const rail = rails.find((r) => r.id === c.bank_account_id);
      const label = rail
        ? `${rail.label} (${providerLabel(rail.provider)})`
        : `rail #${c.bank_account_id}`;
      return `${label}${c.auto_approve ? " · auto" : " · manual"}`;
    })
    .join("  →  ");
}

function modeLabel(mode: 1 | 2 | 3): string {
  return mode === 1 ? "IMPS" : mode === 2 ? "NEFT" : "UPI";
}

// ── Rule form (used both for add + edit) ─────────────────────────────────

function blankRule(): RoutingRule {
  return {
    id: 0,
    merchant_id: 0,
    priority: 100,
    match: {},
    candidates: [{ bank_account_id: 0, auto_approve: false }],
    created_at: "",
    updated_at: "",
  };
}

function RuleForm({
  initial,
  rails,
  merchantId,
  onSaved,
  onCancel,
}: {
  initial: RoutingRule;
  rails: BankAccount[];
  merchantId: number;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const isNew = initial.id === 0;
  const [priority, setPriority] = useState(String(initial.priority));
  const [mode, setMode] = useState<"" | "1" | "2" | "3">(
    initial.match.mode != null ? (String(initial.match.mode) as "1" | "2" | "3") : "",
  );
  const [minRupees, setMinRupees] = useState(
    initial.match.amount_minor_gte != null
      ? String(initial.match.amount_minor_gte / 100)
      : "",
  );
  const [maxRupees, setMaxRupees] = useState(
    initial.match.amount_minor_lte != null
      ? String(initial.match.amount_minor_lte / 100)
      : "",
  );
  // Each row is an editable candidate. `bank_account_id` of 0 means
  // "operator hasn't picked a rail yet" — validation catches it on save.
  const [candidates, setCandidates] = useState<RoutingRuleCandidate[]>(
    initial.candidates.length > 0
      ? initial.candidates.map((c) => ({ ...c }))
      : [{ bank_account_id: 0, auto_approve: false }],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Show a rail in the picker when it's active OR already referenced by
  // one of this rule's candidates (so the operator can still see + fix
  // an existing rule that points at a now-disabled rail).
  const assignedRailIDs = new Set(candidates.map((c) => c.bank_account_id));
  const eligibleRails = rails.filter(
    (r) => r.status === "active" || assignedRailIDs.has(r.id),
  );

  function updateCandidate(idx: number, patch: Partial<RoutingRuleCandidate>) {
    setCandidates((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    );
  }
  function removeCandidate(idx: number) {
    setCandidates((prev) => prev.filter((_, i) => i !== idx));
  }
  function moveCandidate(idx: number, delta: -1 | 1) {
    setCandidates((prev) => {
      const next = idx + delta;
      if (next < 0 || next >= prev.length) return prev;
      const out = prev.slice();
      [out[idx], out[next]] = [out[next], out[idx]];
      return out;
    });
  }
  function addCandidate() {
    setCandidates((prev) => [
      ...prev,
      { bank_account_id: 0, auto_approve: false },
    ]);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const pri = Number(priority);
    if (!Number.isFinite(pri) || !Number.isInteger(pri)) {
      setError("Priority must be a whole number.");
      return;
    }
    if (candidates.length === 0) {
      setError("Add at least one candidate rail.");
      return;
    }
    for (let i = 0; i < candidates.length; i++) {
      if (!candidates[i].bank_account_id) {
        setError(
          `Pick a rail for candidate #${i + 1} (or remove the row).`,
        );
        return;
      }
    }
    const seen = new Set<number>();
    for (const c of candidates) {
      if (seen.has(c.bank_account_id)) {
        setError("Each candidate must be a distinct rail.");
        return;
      }
      seen.add(c.bank_account_id);
    }
    const match: RoutingRuleMatch = {};
    if (mode !== "") match.mode = Number(mode) as 1 | 2 | 3;
    if (minRupees.trim() !== "") {
      const n = Number(minRupees);
      if (!Number.isFinite(n) || n < 0) {
        setError("Minimum amount must be a non-negative number.");
        return;
      }
      match.amount_minor_gte = Math.round(n * 100);
    }
    if (maxRupees.trim() !== "") {
      const n = Number(maxRupees);
      if (!Number.isFinite(n) || n < 0) {
        setError("Maximum amount must be a non-negative number.");
        return;
      }
      match.amount_minor_lte = Math.round(n * 100);
    }
    if (
      match.amount_minor_gte != null &&
      match.amount_minor_lte != null &&
      match.amount_minor_lte < match.amount_minor_gte
    ) {
      setError("Maximum must be ≥ minimum.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        await createRoutingRule(merchantId, {
          priority: pri,
          match,
          candidates,
        });
      } else {
        await updateRoutingRule(merchantId, initial.id, {
          priority: pri,
          match,
          candidates,
        });
      }
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save the rule."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        padding: 12,
        border: "1px solid var(--accent, #0e8a3e)",
        borderRadius: 6,
        background: "var(--bg-sunk)",
      }}
    >
      <div
        className="row gap-8"
        style={{ alignItems: "flex-end", flexWrap: "wrap" }}
      >
        <div className="field" style={{ marginBottom: 0, width: 90 }}>
          <label className="label">Priority</label>
          <input
            className="input"
            type="number"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            min={0}
          />
        </div>
        <div className="field" style={{ marginBottom: 0, width: 130 }}>
          <label className="label">Mode</label>
          <select
            className="input"
            value={mode}
            onChange={(e) => setMode(e.target.value as typeof mode)}
          >
            <option value="">Any</option>
            <option value="1">IMPS</option>
            <option value="2">NEFT</option>
            <option value="3">UPI</option>
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0, width: 130 }}>
          <label className="label">Min ₹ (optional)</label>
          <input
            className="input"
            type="text"
            inputMode="decimal"
            placeholder="0"
            value={minRupees}
            onChange={(e) => setMinRupees(e.target.value)}
          />
        </div>
        <div className="field" style={{ marginBottom: 0, width: 130 }}>
          <label className="label">Max ₹ (optional)</label>
          <input
            className="input"
            type="text"
            inputMode="decimal"
            placeholder="∞"
            value={maxRupees}
            onChange={(e) => setMaxRupees(e.target.value)}
          />
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <div
          className="row gap-8"
          style={{ alignItems: "baseline", marginBottom: 6 }}
        >
          <label className="label" style={{ marginBottom: 0 }}>
            Candidate rails
          </label>
          <span
            className="dim"
            style={{ fontSize: 11, fontWeight: 400 }}
          >
            Walked top → bottom. First with balance ≥ payout wins.
          </span>
        </div>
        <div className="stack-8">
          {candidates.map((c, idx) => (
            <div
              key={idx}
              className="row gap-8"
              style={{
                alignItems: "center",
                padding: "8px 10px",
                border: "1px solid var(--hair)",
                borderRadius: 5,
                background: "var(--bg)",
              }}
            >
              <span
                className="dim"
                style={{
                  fontSize: 11,
                  fontFamily: "var(--font-mono, monospace)",
                  width: 22,
                  textAlign: "right",
                }}
              >
                {idx + 1}.
              </span>
              <select
                className="input"
                value={c.bank_account_id ? String(c.bank_account_id) : ""}
                onChange={(e) =>
                  updateCandidate(idx, {
                    bank_account_id: Number(e.target.value),
                  })
                }
                style={{ flex: 1, minWidth: 180 }}
                required
              >
                <option value="">Select a rail…</option>
                {eligibleRails.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label} · {providerLabel(r.provider)}
                    {r.status !== "active" ? " · (disabled)" : ""}
                  </option>
                ))}
              </select>
              <label
                className="row gap-8"
                style={{
                  alignItems: "center",
                  cursor: "pointer",
                  fontSize: 12,
                  whiteSpace: "nowrap",
                }}
              >
                <input
                  type="checkbox"
                  checked={c.auto_approve}
                  onChange={(e) =>
                    updateCandidate(idx, { auto_approve: e.target.checked })
                  }
                />
                <span>Auto-approve</span>
              </label>
              <button
                type="button"
                className="btn ghost sm"
                disabled={idx === 0}
                onClick={() => moveCandidate(idx, -1)}
                title="Move up"
                style={{ padding: "2px 6px" }}
                aria-label="Move candidate up"
              >
                ↑
              </button>
              <button
                type="button"
                className="btn ghost sm"
                disabled={idx === candidates.length - 1}
                onClick={() => moveCandidate(idx, 1)}
                title="Move down"
                style={{ padding: "2px 6px" }}
                aria-label="Move candidate down"
              >
                ↓
              </button>
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => removeCandidate(idx)}
                disabled={candidates.length === 1}
                title={
                  candidates.length === 1
                    ? "A rule needs at least one candidate"
                    : "Remove candidate"
                }
                aria-label="Remove candidate"
                style={{ padding: "2px 6px" }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="btn ghost sm"
          onClick={addCandidate}
          style={{ marginTop: 8 }}
        >
          <I.Plus size={11} /> Add fallback rail
        </button>
      </div>

      {error && (
        <div
          style={{
            color: "var(--warn, #b42318)",
            fontSize: 12,
            marginTop: 8,
          }}
        >
          {error}
        </div>
      )}

      <div
        className="row gap-8"
        style={{ marginTop: 12, justifyContent: "flex-end" }}
      >
        <button
          type="button"
          className="btn ghost sm"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button className="btn accent sm" type="submit" disabled={saving}>
          {saving ? "Saving…" : isNew ? "Create rule" : "Save changes"}
        </button>
      </div>

      <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
        Rail-balance lookup: IDFC Bank live, RazorpayX live (when the
        rail's account_number matches a valid X virtual account), Buckzy
        deferred. When a candidate's balance can't be verified — provider
        doesn't support balance, misconfigured account, transient outage —
        the rule still dispatches through that candidate with its declared
        auto-approve flag; the provider's own insufficient-funds error
        surfaces normally if the rail really is empty.
      </div>
    </form>
  );
}
